import { useEffect, useRef } from "react";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-premium";
import { DataGridUrlStateManager } from "../DataGridUrlStateManager";

interface UseGridUrlSyncOptions {
  apiRef: React.RefObject<any>;
  urlStateManager: DataGridUrlStateManager;
}

const GROUP_COLUMN_PREFIX = "group-";
const CELL_PADDING = 32; // horizontal padding inside a cell
const ICON_WIDTH = 16; // expand/collapse toggle icon
const INDENT_PER_LEVEL = 24; // indentation added per nesting depth level

/**
 * Works around an internal MUI X DataGrid bug where columns with the 'group-'
 * field prefix are not sized correctly by autosizeColumns. Manually measures
 * each group column's content using a canvas and applies the correct width.
 */
function resizeGroupedColumns(apiRef: React.RefObject<any>): void {
  if (!apiRef.current) return;

  const allColumns = apiRef.current.getAllColumns();
  const groupColumns = allColumns.filter((col: any) =>
    col.field.startsWith(GROUP_COLUMN_PREFIX)
  );

  if (groupColumns.length === 0) return;

  // Measure text with canvas to avoid triggering layout
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Match the DataGrid's default font
  ctx.font = '14px "Roboto", "Helvetica", "Arial", sans-serif';

  const rowIds: any[] = apiRef.current.getAllRowIds();

  groupColumns.forEach((col: any) => {
    let maxWidth: number = col.minWidth ?? 100;

    // Measure the column header label
    const headerText: string = col.headerName || col.field;
    maxWidth = Math.max(
      maxWidth,
      ctx.measureText(headerText).width + CELL_PADDING
    );

    // Measure each group row, accounting for nesting indentation
    rowIds.forEach((rowId) => {
      const rowNode = apiRef.current.getRowNode(rowId);
      if (rowNode?.type === "group") {
        const depth: number = rowNode.depth ?? 0;
        const label = String(rowNode.groupingKey ?? "");
        const textWidth: number = ctx.measureText(label).width;
        const totalWidth: number =
          textWidth + depth * INDENT_PER_LEVEL + ICON_WIDTH + CELL_PADDING;
        maxWidth = Math.max(maxWidth, totalWidth);
      }
    });

    apiRef.current.setColumnWidth(col.field, maxWidth);
  });
}

/**
 * Hook for synchronizing grid state (filter, sort, column visibility) with URL.
 * Also handles auto-sizing columns on visibility changes.
 */
export function useGridUrlSync({
  apiRef,
  urlStateManager,
}: UseGridUrlSyncOptions): void {
  const autosizeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync filter model changes to URL
  useEffect(() => {
    if (!apiRef.current) return;

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
      urlStateManager.writeFilterModelToUrl(newFilterModel);
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "filterModelChange",
      handleFilterModelChange
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef, urlStateManager]);

  // Sync sort model changes to URL
  useEffect(() => {
    if (!apiRef.current) return;

    const handleSortModelChange = (newSortModel: GridSortModel) => {
      urlStateManager.writeSortModelToUrl(newSortModel);
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "sortModelChange",
      handleSortModelChange
    );

    return () => {
      unsubscribe();
    };
  }, [apiRef, urlStateManager]);

  // Auto-size columns when column visibility changes
  useEffect(() => {
    if (!apiRef.current) return;

    const handleAutosize = () => {
      // Clear any existing debounce timeout
      if (autosizeDebounceRef.current) {
        clearTimeout(autosizeDebounceRef.current);
      }

      // Debounce the autosize call to prevent multiple rapid executions
      const timeout = setTimeout(() => {
        if (apiRef.current) {
          const columnsToAutosize = apiRef.current
            .getAllColumns()
            .filter((col: any) => !col.disableAutosize)
            .map((col: any) => col.field);
          apiRef.current
            .autosizeColumns({
              includeOutliers: true,
              columns: columnsToAutosize,
            })
            .then(() => resizeGroupedColumns(apiRef));
        }
      }, 150);

      autosizeDebounceRef.current = timeout;
    };

    // Only subscribe to columnVisibilityModelChange to avoid resize on row selection
    const unsubscribeVisibilityChange = apiRef.current.subscribeEvent(
      "columnVisibilityModelChange",
      handleAutosize
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribeVisibilityChange();
      if (autosizeDebounceRef.current) {
        clearTimeout(autosizeDebounceRef.current);
      }
    };
  }, [apiRef]);
}
